import { SearchableVendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { SrpIndexabilityRequest, StorefrontCardView } from '@zola/svc-marketplace-ts-types';

import queryString from 'query-string';

import { doTrackStorefrontListViewed } from '~/contexts/TrackingContracts/trackingContractsHelpers';
import { getContextStringValue } from '~/libs/client/util/nextUtils';

import {
  buildSearchFilterTracking,
  buildSearchSortTracking,
} from '../../../util/buildSearchFilterTracking';
import { locationAndCategorySlugInPath } from '../../../util/vendorSearchData';
import { VendorSearchRequest, VendorSearchRequestType } from '../types/types';

const SEARCH_PATH_REGEX = /\/wedding-vendors\/(search|near-me)\//;

/**
 * Parse the slug and query params from a search results page URL, and make sure that query params have no null values
 */
export const parseUrlValues = (url: string) => {
  const { url: path, query } = queryString.parseUrl(url, { arrayFormat: 'bracket' });
  const slug = path.split('/').pop() || path;
  const queryParams: Record<string, string | string[]> = {};

  Object.entries(query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      queryParams[key] = value.filter(Boolean) as string[];
    } else {
      queryParams[key] = value || '';
    }
  });

  return { slug, queryParams };
};

/**
 * Get the page type, slug, and query params from a Next context query object
 */
export const getQueryValues = (query: Record<string, string | string[] | undefined>) => {
  let pageType = 'srp';
  let slug = '';
  const queryParams: Record<string, string | string[]> = {};

  Object.entries(query).forEach(([key, value]) => {
    switch (key) {
      case 'pageType':
        pageType = getContextStringValue(value) || '';
        break;
      case 'slug':
        slug = getContextStringValue(value) || '';
        break;
      default:
        queryParams[key] = value || '';
    }
  });

  return { pageType, slug, queryParams };
};

/**
 * Get the location, taxonomy key, and facet slugs from a search slug
 */
export const getSearchSlugs = (slug: string) => {
  const pathSlugs = slug.split('--').filter(Boolean);
  const { location, taxonomyKey } = locationAndCategorySlugInPath(pathSlugs);
  const facetSlugs = pathSlugs.filter(
    (pathSlug) => pathSlug !== location && pathSlug !== taxonomyKey
  );
  return {
    location,
    taxonomyKey,
    facetSlugs,
  };
};

/**
 * Get the indexability request object for an SRP data endpoint call
 */
export const getIndexabilityRequest = (
  location: string | null,
  taxonomyKey: string | null,
  facetSlugs: string[] | null,
  queryParams: Record<string, string | string[]>,
  pageType?: string
): SrpIndexabilityRequest => {
  // Convert every query param value to an array, because the service can't accept both strings and arrays
  const mappedQueryParams = Object.entries(queryParams).reduce(
    (accumulator, [key, value]) => ({
      ...accumulator,
      [key]: [value].flat(),
    }),
    {}
  );
  return {
    locationSlug: location,
    taxonomyKey,
    facetSlugs,
    queryParams: mappedQueryParams,
    pageType: pageType === 'near-me' ? 'NEAR_ME' : 'DEFAULT_SRP',
  };
};

/**
 * Determines if the page should redirect to the canonical URL
 *
 * @returns True if the search slug does not match the canonical search slug; false otherwise
 */
export const shouldRedirect = (url: string, canonicalUrl: string) => {
  if (!url || !canonicalUrl) {
    return false;
  }
  const searchSlug = url.replace(SEARCH_PATH_REGEX, '').split('?')[0];
  const canonicalSearchSlug = canonicalUrl.replace(SEARCH_PATH_REGEX, '').split('?')[0];
  return searchSlug !== canonicalSearchSlug;
};

/**
 * Track product list views on page load and follow-up searches
 */
export const trackVendorSearch = (
  vendorSearch: VendorSearchRequest,
  searchRequest: VendorSearchRequestType,
  storefrontList: StorefrontCardView[]
) => {
  const taxonomyKey = vendorSearch.selectedTaxonomy?.key as SearchableVendorTaxonomyKey | undefined;

  const trackingFilters = buildSearchFilterTracking(
    searchRequest,
    vendorSearch.selectedLocation,
    vendorSearch.selectedFacets
  );

  const trackingSort = buildSearchSortTracking(
    vendorSearch.sorting,
    taxonomyKey,
    vendorSearch.selectedLocation
  );

  doTrackStorefrontListViewed({
    storefrontList,
    filters: trackingFilters,
    taxonomyKey,
    sorts: trackingSort ? [trackingSort] : [],
    listingShelfName: 'SEARCH_RESULTS_PAGE',
    includeTags: true,
  });
};
